import React, { useState, useEffect } from 'react';
import {
  H2,
  H3,
  H4,
  Card,
  Tag,
  Intent,
  Callout,
  Icon,
  Dialog,
  DialogBody,
  DialogFooter,
  Button,
  HTMLSelect,
  InputGroup
} from '@blueprintjs/core';
import { subscribeToActHistory } from '../firebase/actService';
import { calculatePlayerElo, calculateTeamElo, getEloConfidence, processActTournament, ELO_CONFIG } from '../utils/eloCalculator';
import { initializeEloFromHistory, getCurrentEloRatings, getEloTier, recalculateEloFromHistory } from '../firebase/eloHistoricalCalculator';
import { subscribeToEloRatings, getEloLeaderboard, getPlayersWithEloRatings, getAllPlayerStatuses, subscribeToPlayerStatuses } from '../firebase/eloManagement';
import { getPlayerEloStats, fetchMultipleEloHistories } from '../firebase/eloService';
import { TOP_BRACKET_CUPS as CUPS, placeToPoints } from '../constants/cups';
import EloAdminPanel from '../components/EloAdminPanel';
import EloTestPanel from '../components/EloTestPanel';

const HISTORY_KEY = 'ACT_SCORECARD_HISTORY';
const TEAMS = ['A', 'B', 'C', 'D'];

function normalizePlayerName(value) {
  return typeof value === 'string' ? value.toLowerCase().trim() : '';
}

function extractRaceParticipants(entry, race) {
  return Object.entries(race || {})
    .map(([teamKey, raceData]) => {
      const rawPlayerName = typeof raceData?.racer === 'string' ? raceData.racer.trim() : '';
      const rosterPlayerName = entry?.teams?.[teamKey]?.players?.[rawPlayerName];
      const playerName =
        rawPlayerName === 'A' || rawPlayerName === 'B' || rawPlayerName === 'C'
          ? typeof rosterPlayerName === 'string'
            ? rosterPlayerName.trim()
            : ''
          : rawPlayerName;
      const place = Number(raceData?.place);

      return {
        playerName,
        place,
      };
    })
    .filter(({ playerName, place }) => playerName && Number.isFinite(place));
}

function Analytics() {
  const [history, setHistory] = useState([]);
  const [playerStats, setPlayerStats] = useState([]);
  const [filteredPlayerStats, setFilteredPlayerStats] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [firebaseEloRatings, setFirebaseEloRatings] = useState({});
  const [eloHistories, setEloHistories] = useState({});
  const [realtimeEloRatings, setRealtimeEloRatings] = useState({});
  const [playersWithElo, setPlayersWithElo] = useState({});
  const [playerStatuses, setPlayerStatuses] = useState({});
  const [eloChange30dByPlayer, setEloChange30dByPlayer] = useState({});
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'retired'

  // Sort state
  const [sortBy, setSortBy] = useState('elo');

  // Admin panel visibility (show with ?admin=true in URL and correct password)
  const [showAdmin, setShowAdmin] = useState(false);

  const [isAdminModalOpen, setIsAdminModalOpen] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('admin') === 'true';
  });
  const [adminPassword, setAdminPassword] = useState('');

  useEffect(() => {
    // Subscribe to Firebase real-time updates
    const unsubscribeHistory = subscribeToActHistory(async (firebaseHistory) => {
      console.log('Received history update from Firebase:', firebaseHistory.length, 'entries');
      setHistory(firebaseHistory);

      // Get players with their actual ELO ratings
      const playersElo = await getPlayersWithEloRatings(firebaseHistory);
      setPlayersWithElo(playersElo);

      // Get current ELO ratings from Firebase (without recalculating all history)
      console.log('🔥🔥🔥 LOADING CURRENT ELO RATINGS FROM FIREBASE 🔥🔥🔥');
      const currentEloRatings = await getCurrentEloRatings();
      console.log('🔥🔥🔥 LOADED ELO RATINGS:', Object.keys(currentEloRatings).length, 'PLAYERS WITH RATINGS 🔥🔥🔥');
      setFirebaseEloRatings(currentEloRatings);

      // Calculate ELO histories from historical data
      console.log('📊 Calculating ELO histories from', firebaseHistory.length, 'tournaments');
      const { ratings, histories } = await recalculateEloFromHistory(firebaseHistory, currentEloRatings);
      setEloHistories(histories);
      console.log('📊 Generated ELO histories for', Object.keys(histories).length, 'players');


      calculatePlayerStats(firebaseHistory, currentEloRatings, histories, playersElo);
    });

    // Subscribe to real-time ELO updates
    const unsubscribeElo = subscribeToEloRatings((ratings) => {
      console.log('📊 Real-time ELO update received');
      setRealtimeEloRatings(ratings);
    });

    // Subscribe to player status updates
    const unsubscribeStatuses = subscribeToPlayerStatuses((statuses) => {
      console.log('📊 Player statuses update received');
      setPlayerStatuses(statuses);
    });

    // Load initial player statuses
    getAllPlayerStatuses().then(statuses => {
      setPlayerStatuses(statuses);
    });

    // Cleanup subscriptions on unmount
    return () => {
      if (unsubscribeHistory) {
        unsubscribeHistory();
      }
      if (unsubscribeElo) {
        unsubscribeElo();
      }
      if (unsubscribeStatuses) {
        unsubscribeStatuses();
      }
    };
  }, []);

  // Recalculate player stats when realtime ELO updates
  useEffect(() => {
    if (history.length > 0 && Object.keys(realtimeEloRatings).length > 0) {
      console.log('📊 Recalculating stats with realtime ELO');
      // Recalculate ELO histories with new ratings
      recalculateEloFromHistory(history, realtimeEloRatings).then(({ ratings, histories }) => {
        setEloHistories(histories);
        // Get updated player-ELO mappings when ELO data changes
        getPlayersWithEloRatings(history).then(updatedPlayersElo => {
          setPlayersWithElo(updatedPlayersElo);
          calculatePlayerStats(history, realtimeEloRatings, histories, updatedPlayersElo);
        });
      });
    }
  }, [realtimeEloRatings, history]);

  useEffect(() => {
    if (playerStats.length === 0) {
      setEloChange30dByPlayer({});
      return;
    }

    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - THIRTY_DAYS_MS;

    const calculate30dDelta = (historyEntries = []) => {
      const validEntries = historyEntries
        .map(entry => ({
          at: Number(entry.at),
          value: Number(entry.value)
        }))
        .filter(entry => Number.isFinite(entry.at) && Number.isFinite(entry.value))
        .sort((a, b) => a.at - b.at);

      if (validEntries.length === 0) return null;

      const currentEntry = validEntries[validEntries.length - 1];
      let baselineEntry = null;

      for (let i = validEntries.length - 1; i >= 0; i--) {
        if (validEntries[i].at <= cutoff) {
          baselineEntry = validEntries[i];
          break;
        }
      }

      if (!baselineEntry) {
        baselineEntry = validEntries.find(entry => entry.at >= cutoff) || currentEntry;
      }

      return currentEntry.value - baselineEntry.value;
    };

    const resolveEloHistoryKey = (player) => {
      const mappedEloKey = playersWithElo[player.name]?.eloKey;
      if (mappedEloKey) return mappedEloKey;

      if (typeof player.displayName === 'string') {
        const match = player.displayName.match(/^team_([a-d])_player_([abc])$/i);
        if (match) {
          return `${match[1].toUpperCase()}_${match[2].toUpperCase()}`;
        }
      }

      return player.hasEloRating ? player.name : null;
    };

    const targets = playerStats.map(player => ({
      rowKey: player.displayName,
      historyKey: resolveEloHistoryKey(player)
    }));

    const uniqueHistoryKeys = [...new Set(targets.map(target => target.historyKey).filter(Boolean))];

    if (uniqueHistoryKeys.length === 0) {
      setEloChange30dByPlayer({});
      return;
    }

    let isCancelled = false;

    fetchMultipleEloHistories(uniqueHistoryKeys)
      .then((histories) => {
        if (isCancelled) return;

        const nextChanges = {};
        targets.forEach(({ rowKey, historyKey }) => {
          nextChanges[rowKey] = historyKey && histories[historyKey]
            ? calculate30dDelta(histories[historyKey])
            : null;
        });

        setEloChange30dByPlayer(nextChanges);
      })
      .catch((error) => {
        console.error('Error loading leaderboard 30-day ELO changes:', error);
        if (!isCancelled) {
          setEloChange30dByPlayer({});
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [playerStats, playersWithElo]);

  // Keep this function for potential fallback to localStorage
  const loadHistoryAndCalculatePlayerStats = () => {
    const savedHistory = localStorage.getItem(HISTORY_KEY);
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(parsedHistory);
        calculatePlayerStats(parsedHistory, {}, {}, playersWithElo);
      } catch (error) {
        console.error('Error loading history:', error);
        setHistory([]);
      }
    }
  };

  const calculatePlayerStats = (historyData, eloRatings = {}, eloHistoriesData = {}, playersEloData = {}) => {
    if (historyData.length === 0) {
      setPlayerStats([]);
      return;
    }

    console.log('🏁 Calculating player stats with ELO for', historyData.length, 'tournaments');

    const playerData = {};
    const tournamentHistory = {}; // Track each player's tournament history for ELO calculation
    const playerEloHistories = {}; // Track ELO changes over time for each player

    // Process each ACT entry
    historyData.forEach((entry, entryIndex) => {
      // Calculate tournament rankings for this ACT
      const teamRankings = TEAMS.map(team => ({
        team,
        totalPoints: entry.totals[team] || 0
      })).sort((a, b) => b.totalPoints - a.totalPoints);

      // Determine tournament winners and top 2 finishers
      const maxPoints = teamRankings[0].totalPoints;
      const tournamentWinners = teamRankings.filter(t => t.totalPoints === maxPoints).map(t => t.team);
      const top2Teams = teamRankings.slice(0, 2).map(t => t.team);

      // Get tournament placement for each team
      const getTeamTournamentPlace = (team) => {
        const teamRank = teamRankings.findIndex(t => t.team === team) + 1;
        return teamRank;
      };

      TEAMS.forEach(team => {
        // Check if this is a 12-person ACT by looking for Player C
        const playerC = entry.teams[team]?.players?.['C'];
        const is12PersonACT = playerC && playerC !== '';
        const playerKeys = is12PersonACT ? ['A', 'B', 'C'] : ['A', 'B'];

        playerKeys.forEach(player => {
          const playerName = entry.teams[team]?.players?.[player] || `Player ${player}`;

          // Normalize player name to handle case/whitespace variations
          const normalizedName = normalizePlayerName(playerName);
          const isDefaultPlayer = normalizedName === `player ${player.toLowerCase()}`;

          // Use normalized name as unique key, but preserve original for display
          const playerKey = isDefaultPlayer ? `team_${team.toLowerCase()}_player_${player.toLowerCase()}` : normalizedName;

          if (!playerData[playerKey]) {
            playerData[playerKey] = {
              name: playerName,
              displayName: isDefaultPlayer ? playerKey : playerName,
              totalPoints: 0,
              jerseySwaps: 0, // Top 2 tournament finishes
              wins: 0, // Tournament victories
              racesPlayed: 0,
              tournamentsPlayed: 0,
              elo: ELO_CONFIG.DEFAULT_RATING, // Will be calculated properly below
              // Race placement counts
              raceFirst: 0,
              raceSecond: 0,
              raceThird: 0,
              raceFourth: 0,
              // Tournament placement counts
              tournamentFirst: 0,
              tournamentSecond: 0,
              tournamentThird: 0,
              tournamentFourth: 0,
              // Character usage tracking
              characterUsage: {},
              rivalMatchups: {}
            };
          } else {
            // If player exists, update display name if we found a better one
            if (!isDefaultPlayer && playerData[playerKey].displayName.includes('team_')) {
              playerData[playerKey].name = playerName;
              playerData[playerKey].displayName = playerName;
            }
          }

          // Track tournaments played
          playerData[playerKey].tournamentsPlayed++;

          // Track character usage
          const teamCharacter = entry.teams[team]?.character;
          if (teamCharacter && teamCharacter !== '') {
            if (!playerData[playerKey].characterUsage[teamCharacter]) {
              playerData[playerKey].characterUsage[teamCharacter] = 0;
            }
            playerData[playerKey].characterUsage[teamCharacter]++;
          }

          // Track tournament placements for display only (not used in ELO)
          const tournamentPlace = getTeamTournamentPlace(team);
          if (tournamentPlace === 1) playerData[playerKey].tournamentFirst++;
          else if (tournamentPlace === 2) playerData[playerKey].tournamentSecond++;
          else if (tournamentPlace === 3) playerData[playerKey].tournamentThird++;
          else if (tournamentPlace === 4) playerData[playerKey].tournamentFourth++;

          // Award tournament wins and jersey swaps for display only (not used in ELO)
          if (tournamentWinners.includes(team)) {
            playerData[playerKey].wins++;
          }
          if (top2Teams.includes(team)) {
            playerData[playerKey].jerseySwaps++;
          }

          // Track tournament history for ELO calculation
          if (!tournamentHistory[playerKey]) {
            tournamentHistory[playerKey] = [];
          }

          tournamentHistory[playerKey].push({
            date: entry.date,
            team: team,
            player: player, // Add explicit player letter (A or B)
            teamPlacements: {
              A: getTeamTournamentPlace('A'),
              B: getTeamTournamentPlace('B'),
              C: getTeamTournamentPlace('C'),
              D: getTeamTournamentPlace('D')
            },
            totalPoints: entry.totals[team] || 0,
            tournamentPlace: tournamentPlace
          });

          // Build ELO history entry for this player if they have a rating
          if (playerKey && playerData[playerKey]) {
            if (!playerEloHistories[playerKey]) {
              playerEloHistories[playerKey] = [];
            }

            // Add tournament-level ELO history entry if player has ELO
            const playerEloKey = normalizedName === `player ${player.toLowerCase()}` ?
              `${team}_${player}` : normalizedName;

            if (eloRatings[playerEloKey] || eloRatings[playerKey] || eloRatings[playerName]) {
              const currentElo = eloRatings[playerEloKey] || eloRatings[playerKey] || eloRatings[playerName];

              // Calculate what the previous ELO would have been
              const previousEntries = playerEloHistories[playerKey];
              const previousElo = previousEntries.length > 0 ?
                previousEntries[previousEntries.length - 1].elo :
                ELO_CONFIG.DEFAULT_RATING;

              playerEloHistories[playerKey].push({
                date: entry.date || new Date(entry.timestamp).toISOString(),
                elo: Math.round(currentElo),
                change: Math.round(currentElo - previousElo),
                event: `ACT Tournament`,
                finish: tournamentPlace,
                cup: 'Tournament',
                raceNum: null
              });
            }
          }

          // Count races and calculate individual race points for this player
          // Only process cups that actually have data in this ACT entry
          // An ACT should have either top bracket OR bottom bracket, not both
          const activeCups = CUPS.filter(cup =>
            entry.races[cup] && Object.keys(entry.races[cup]).length > 0
          );

          // Debug logging for first few entries
          if (entryIndex < 3) {
            console.log(`🏁 ACT ${entryIndex + 1} active cups:`, activeCups, `(${activeCups.length} cups)`);
          }

          activeCups.forEach(cup => {

            // Get all available race numbers for this cup (handles both 4 and 6 race formats)
            const availableRaces = Object.keys(entry.races[cup] || {}).map(num => parseInt(num)).sort((a, b) => a - b);

            availableRaces.forEach(raceNum => {

              const raceData = entry.races[cup][raceNum][team];
              // Only count this race if this specific player raced it
              // Check both player name and player letter (A/B/C) for compatibility
              if (raceData && (raceData.racer === playerName || raceData.racer === player)) {
                // Skip void races for statistics (but could track separately)
                if (raceData.place === 'X') {
                  // Still count as a race played, just no points
                  playerData[playerKey].racesPlayed++;
                  return;
                }

                // Count this as a race played by this player
                playerData[playerKey].racesPlayed++;

                // Use unified place to points mapping
                const points = placeToPoints(raceData.place);
                playerData[playerKey].totalPoints += points;

                // Track race placement statistics
                if (raceData.place === 1) playerData[playerKey].raceFirst++;
                else if (raceData.place === 2) playerData[playerKey].raceSecond++;
                else if (raceData.place === 3) playerData[playerKey].raceThird++;
                else if (raceData.place === 4) playerData[playerKey].raceFourth++;

                const selectedPlace = Number(raceData.place);
                const participants = extractRaceParticipants(entry, entry.races[cup][raceNum]);

                participants.forEach(({ playerName: opponentName, place: opponentPlace }) => {
                  const normalizedOpponentName = normalizePlayerName(opponentName);

                  if (
                    !normalizedOpponentName ||
                    normalizedOpponentName === normalizedName ||
                    opponentPlace === selectedPlace
                  ) {
                    return;
                  }

                  if (!playerData[playerKey].rivalMatchups[normalizedOpponentName]) {
                    playerData[playerKey].rivalMatchups[normalizedOpponentName] = {
                      opponent: opponentName,
                      wins: 0,
                      losses: 0,
                      total: 0
                    };
                  }

                  const matchup = playerData[playerKey].rivalMatchups[normalizedOpponentName];
                  if (matchup.opponent.length < opponentName.length) {
                    matchup.opponent = opponentName;
                  }

                  if (selectedPlace < opponentPlace) {
                    matchup.wins++;
                  } else {
                    matchup.losses++;
                  }

                  matchup.total = matchup.wins + matchup.losses;
                });
              }
            });
          });
        });
      });
    });

    // Use Firebase ELO ratings
    console.log('🎯 Using Firebase ELO ratings for all players');
    Object.entries(playerData).forEach(([playerKey, player]) => {
      // Skip undefined/invalid player keys
      if (!playerKey || typeof playerKey !== 'string') {
        console.log('⚠️ Skipping invalid player key:', playerKey);
        return;
      }

      // Map player key to Firebase player ID format
      let firebasePlayerId = null;

      // Check if this is a default player (team_x_player_y format)
      if (playerKey.includes('team_')) {
        // Extract team and player from the key
        const match = playerKey.match(/team_([a-d])_player_([ab])/i);
        if (match) {
          const team = match[1].toUpperCase();
          const playerLetter = match[2].toUpperCase();
          firebasePlayerId = `${team}_${playerLetter}`;
        }
      } else {
        // For named players, try to find them in the tournament history
        const tourHistory = tournamentHistory[playerKey];
        if (tourHistory && tourHistory.length > 0) {
          const firstEntry = tourHistory[0];
          firebasePlayerId = `${firstEntry.team}_${firstEntry.player}`;
        }
      }

      // Get ELO from our historically calculated ratings (with reduced impact)
      const normalizedPlayerName = player.name.toLowerCase().trim();

      // Try to find the player's calculated ELO rating
      let calculatedElo = null;

      // First try direct match with calculated ELO ratings
      if (eloRatings[playerKey]) {
        calculatedElo = eloRatings[playerKey];
      } else if (eloRatings[player.name]) {
        calculatedElo = eloRatings[player.name];
      } else {
        // Try normalized name match
        const matchingKey = Object.keys(eloRatings).find(key =>
          key.toLowerCase().trim() === normalizedPlayerName
        );
        if (matchingKey) {
          calculatedElo = eloRatings[matchingKey];
        }
      }

      if (calculatedElo !== null && calculatedElo !== undefined) {
        // Player has a calculated ELO rating (from database + historical impact)
        playerData[playerKey].elo = Math.round(calculatedElo);
        playerData[playerKey].eloHistory = playerEloHistories[playerKey] || eloHistoriesData[playerKey] || [];
        playerData[playerKey].eloTier = getEloTier(calculatedElo);
        playerData[playerKey].eloConfidence = getEloConfidence(player.tournamentsPlayed);
        playerData[playerKey].hasEloRating = true;

        console.log(`🎯 ${playerKey} (${player.name}): CALCULATED ELO = ${Math.round(calculatedElo)} with ${playerData[playerKey].eloHistory.length} history entries`);
      } else {
        // No calculated ELO rating found, check playersEloData for Firebase ELO
        let firebaseElo = null;

        // Check if this player has an ELO in the playersEloData mapping
        if (playersEloData[player.name] && playersEloData[player.name].elo) {
          firebaseElo = playersEloData[player.name].elo;
          console.log(`📊 Found Firebase ELO for ${player.name}: ${firebaseElo}`);
        }

        if (firebaseElo !== null) {
          // Player has a Firebase ELO but no calculated history
          playerData[playerKey].elo = Math.round(firebaseElo);
          playerData[playerKey].eloHistory = [];
          playerData[playerKey].eloTier = getEloTier(firebaseElo);
          playerData[playerKey].eloConfidence = getEloConfidence(player.tournamentsPlayed);
          playerData[playerKey].hasEloRating = true;
          playerData[playerKey].fromFirebase = true;

          console.log(`✅ ${playerKey} (${player.name}): Using Firebase ELO = ${Math.round(firebaseElo)}`);
        } else {
          // No ELO found anywhere
          playerData[playerKey].elo = null;
          playerData[playerKey].eloHistory = [];
          playerData[playerKey].eloTier = { name: 'Unrated', color: '#9E9E9E', icon: '❓' };
          playerData[playerKey].eloConfidence = 'Unrated';
          playerData[playerKey].hasEloRating = false;

          console.log(`❌ ${playerKey} (${player.name}): No ELO rating found - marked as N/A`);
        }
      }
    });

    // Calculate averages and sort
    const players = Object.values(playerData).map(player => {
      // Calculate favorite character
      let favoriteCharacter = 'None';
      let favoriteCharacterCount = 0;
      let biggestRival = 'None';
      let biggestRivalRaces = 0;
      let biggestRivalWins = 0;
      let biggestRivalLosses = 0;

      if (Object.keys(player.characterUsage).length > 0) {
        const mostUsedEntry = Object.entries(player.characterUsage).reduce((max, [char, count]) =>
          count > max[1] ? [char, count] : max
        );
        favoriteCharacter = mostUsedEntry[0];
        favoriteCharacterCount = mostUsedEntry[1];
      }

      const rivalCandidates = Object.values(player.rivalMatchups || {}).filter((matchup) => {
        if (!matchup.total) {
          return false;
        }

        const winShare = matchup.wins / matchup.total;
        return winShare >= 0.4 && winShare <= 0.6;
      });

      if (rivalCandidates.length > 0) {
        const bestRival = rivalCandidates.sort((a, b) => {
          const totalDiff = b.total - a.total;
          if (totalDiff !== 0) {
            return totalDiff;
          }

          const balanceDiff = Math.abs(a.wins / a.total - 0.5) - Math.abs(b.wins / b.total - 0.5);
          if (balanceDiff !== 0) {
            return balanceDiff;
          }

          return a.opponent.localeCompare(b.opponent);
        })[0];

        biggestRival = bestRival.opponent;
        biggestRivalRaces = bestRival.total;
        biggestRivalWins = bestRival.wins;
        biggestRivalLosses = bestRival.losses;
      }

      return {
        ...player,
        favoriteCharacter,
        favoriteCharacterCount,
        biggestRival,
        biggestRivalRaces,
        biggestRivalWins,
        biggestRivalLosses,
        avgPoints: player.racesPlayed > 0 ? ((player.totalPoints / player.racesPlayed) * 8).toFixed(2) : '0.00',
        winRate: player.tournamentsPlayed > 0 ? ((player.wins / player.tournamentsPlayed) * 100).toFixed(1) : '0.0',
        jerseySwapRate: player.tournamentsPlayed > 0 ? ((player.jerseySwaps / player.tournamentsPlayed) * 100).toFixed(1) : '0.0',
        // Race percentages
        raceFirstPct: player.racesPlayed > 0 ? ((player.raceFirst / player.racesPlayed) * 100).toFixed(1) : '0.0',
        raceSecondPct: player.racesPlayed > 0 ? ((player.raceSecond / player.racesPlayed) * 100).toFixed(1) : '0.0',
        raceThirdPct: player.racesPlayed > 0 ? ((player.raceThird / player.racesPlayed) * 100).toFixed(1) : '0.0',
        raceFourthPct: player.racesPlayed > 0 ? ((player.raceFourth / player.racesPlayed) * 100).toFixed(1) : '0.0',
        // Tournament percentages
        tournamentFirstPct: player.tournamentsPlayed > 0 ? ((player.tournamentFirst / player.tournamentsPlayed) * 100).toFixed(1) : '0.0',
        tournamentSecondPct: player.tournamentsPlayed > 0 ? ((player.tournamentSecond / player.tournamentsPlayed) * 100).toFixed(1) : '0.0',
        tournamentThirdPct: player.tournamentsPlayed > 0 ? ((player.tournamentThird / player.tournamentsPlayed) * 100).toFixed(1) : '0.0',
        tournamentFourthPct: player.tournamentsPlayed > 0 ? ((player.tournamentFourth / player.tournamentsPlayed) * 100).toFixed(1) : '0.0'
      };
    });

    setPlayerStats(players);
  };

  const getRankIcon = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `#${index + 1}`;
  };

  const getRankColor = (index) => {
    if (index === 0) return 'var(--success)';
    if (index === 1) return 'var(--warning)';
    if (index === 2) return '#cd7f32';
    return 'var(--text-secondary)';
  };

  const getPlayerEloChange30d = (player) => {
    const value = eloChange30dByPlayer[player.displayName];
    return Number.isFinite(value) ? value : null;
  };

  const eloRankChange30dByPlayer = (() => {
    const comparablePlayers = playerStats
      .filter(player => player.hasEloRating && player.elo !== null)
      .map(player => ({
        player,
        change30d: getPlayerEloChange30d(player)
      }))
      .filter(entry => entry.change30d !== null);

    if (comparablePlayers.length === 0) {
      return {};
    }

    const sortByCurrentElo = [...comparablePlayers].sort((a, b) => {
      if (b.player.elo !== a.player.elo) return b.player.elo - a.player.elo;
      return a.player.displayName.localeCompare(b.player.displayName);
    });

    const sortByPriorElo = [...comparablePlayers].sort((a, b) => {
      const aPriorElo = a.player.elo - a.change30d;
      const bPriorElo = b.player.elo - b.change30d;
      if (bPriorElo !== aPriorElo) return bPriorElo - aPriorElo;
      return a.player.displayName.localeCompare(b.player.displayName);
    });

    const currentRank = {};
    const priorRank = {};

    sortByCurrentElo.forEach((entry, index) => {
      currentRank[entry.player.displayName] = index + 1;
    });

    sortByPriorElo.forEach((entry, index) => {
      priorRank[entry.player.displayName] = index + 1;
    });

    const deltas = {};
    comparablePlayers.forEach(({ player }) => {
      deltas[player.displayName] = priorRank[player.displayName] - currentRank[player.displayName];
    });

    return deltas;
  })();

  const getPlayerEloRankChange30d = (player) => {
    const value = eloRankChange30dByPlayer[player.displayName];
    return Number.isFinite(value) ? value : null;
  };

  const handlePlayerClick = async (player) => {
    // Try to find the correct Firebase key for this player
    let firebaseKey = player.name;

    // Check if we have the player in our playersWithElo mapping
    if (playersWithElo[player.name] && playersWithElo[player.name].eloKey) {
      firebaseKey = playersWithElo[player.name].eloKey;
    }

    // Fetch real ELO stats from Firebase using the correct key
    const eloStats = await getPlayerEloStats(firebaseKey);

    // Always use the player's calculated ELO from the leaderboard as primary
    // The Firebase stats are just for history information
    let finalElo = player.elo;
    let hasEloRating = player.hasEloRating;

    // If player has no calculated ELO but Firebase has one, use it
    if ((!player.hasEloRating || player.elo === null) && eloStats.current && !eloStats.isDefault) {
      finalElo = eloStats.current;
      hasEloRating = true;
    }

    // Update player object with real Firebase ELO history stats
    const enrichedPlayer = {
      ...player,
      firebaseEloStats: eloStats,
      hasFirebaseHistory: eloStats.historyCount > 0,
      // Use the final determined ELO value
      elo: finalElo,
      eloTier: hasEloRating ? getEloTier(finalElo) : player.eloTier,
      hasEloRating: hasEloRating
    };

    setSelectedPlayer(enrichedPlayer);
    setIsPlayerModalOpen(true);
  };

  const closePlayerModal = () => {
    setIsPlayerModalOpen(false);
    setSelectedPlayer(null);
  };

  const handleAdminPasswordSubmit = () => {
    if (adminPassword === process.env.REACT_APP_ADMIN_PASSWORD) {
      setShowAdmin(true);
      setIsAdminModalOpen(false);
      setAdminPassword('');
    } else {
      alert('Incorrect password. Access denied.');
      setAdminPassword('');
    }
  };

  const closeAdminModal = () => {
    setIsAdminModalOpen(false);
    setAdminPassword('');
    // Remove admin parameter from URL
    const url = new URL(window.location);
    url.searchParams.delete('admin');
    window.history.replaceState({}, '', url);
  };


  // Sort and filter players
  useEffect(() => {
    let sorted = [...playerStats];

    // Apply status filter
    if (statusFilter !== 'all') {
      sorted = sorted.filter(player => {
        const status = playerStatuses[player.name] || 'active';
        return status === statusFilter;
      });
    }

    // Sort by selected criteria
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'totalPoints':
          return b.totalPoints - a.totalPoints;
        case 'jerseySwaps':
          return b.jerseySwaps - a.jerseySwaps;
        case 'wins':
          return b.wins - a.wins;
        case 'tournamentsPlayed':
          return b.tournamentsPlayed - a.tournamentsPlayed;
        case 'elo':
          // Handle null ELO values - put them at the bottom
          if (a.elo === null && b.elo === null) return 0;
          if (a.elo === null) return 1; // Move to bottom
          if (b.elo === null) return -1; // Move to bottom
          return b.elo - a.elo;
        case 'winRate':
          return parseFloat(b.winRate) - parseFloat(a.winRate);
        case 'jerseySwapRate':
          return parseFloat(b.jerseySwapRate) - parseFloat(a.jerseySwapRate);
        case 'avgPoints':
          return parseFloat(b.avgPoints) - parseFloat(a.avgPoints);
        case 'eloChange30d': {
          const aRaw = eloChange30dByPlayer[a.displayName];
          const bRaw = eloChange30dByPlayer[b.displayName];
          const aChange = Number.isFinite(aRaw) ? aRaw : null;
          const bChange = Number.isFinite(bRaw) ? bRaw : null;
          if (aChange === null && bChange === null) return 0;
          if (aChange === null) return 1;
          if (bChange === null) return -1;
          return bChange - aChange;
        }
        default:
          return b.totalPoints - a.totalPoints;
      }
    });

    setFilteredPlayerStats(sorted);
  }, [playerStats, sortBy, statusFilter, playerStatuses, eloChange30dByPlayer]);

  if (history.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <H2 style={{ margin: '0 0 1rem 0' }}>Player Analytics</H2>
        <Callout intent={Intent.PRIMARY} title="No Data Available">
          Complete some ACT scorecards to see player analytics and statistics!
        </Callout>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <H2 style={{ margin: 0 }}>Player Analytics</H2>
        <Tag intent={Intent.PRIMARY}>{history.length} ACT{history.length !== 1 ? 's' : ''} Analyzed</Tag>
      </div>

      {/* Stats Overview */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <Card style={{ padding: '1rem', textAlign: 'center', background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))' }}>
          <Icon icon="people" size={24} style={{ color: 'var(--accent-primary)', marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-primary)' }}>
            {playerStats.length}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Active Players</div>
        </Card>

        <Card style={{ padding: '1rem', textAlign: 'center', background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))' }}>
          <Icon icon="chart" size={24} style={{ color: 'var(--success)', marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)' }}>
            {playerStats.reduce((sum, p) => sum + p.totalPoints, 0)}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Total Points</div>
        </Card>

        <Card style={{ padding: '1rem', textAlign: 'center', background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))' }}>
          <Icon icon="crown" size={24} style={{ color: 'var(--warning)', marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>
            {playerStats.reduce((sum, p) => sum + p.wins, 0)}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Total Wins</div>
        </Card>

        <Card style={{ padding: '1rem', textAlign: 'center', background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))' }}>
          <Icon icon="exchange" size={24} style={{ color: 'var(--accent-secondary)', marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-secondary)' }}>
            {playerStats.reduce((sum, p) => sum + p.jerseySwaps, 0)}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Jersey Swaps</div>
        </Card>

      </div>


      {/* ELO Admin Panel */}
      {showAdmin && <EloAdminPanel />}

      {/* ELO Test Panel */}
      {showAdmin && <EloTestPanel />}


      {/* Player Leaderboard */}
      <Card style={{ padding: '1.5rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <H3 style={{
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Icon icon="trophy" size={20} />
            Player Leaderboard
            <Tag intent={Intent.PRIMARY}>
              {playerStats.length} players
            </Tag>
          </H3>
        </div>

        {/* Sort Controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            Sort By:
          </span>
          <HTMLSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            options={[
              { label: 'Total Points', value: 'totalPoints' },
              { label: 'Jersey Swaps', value: 'jerseySwaps' },
              { label: 'Wins', value: 'wins' },
              { label: 'Total ACTs', value: 'tournamentsPlayed' },
              { label: 'ELO', value: 'elo' },
              { label: 'ELO Change (30d)', value: 'eloChange30d' },
              { label: 'Win Rate %', value: 'winRate' },
              { label: 'Jersey Swap Rate %', value: 'jerseySwapRate' },
              { label: 'Avg Points / ACT', value: 'avgPoints' }
            ]}
            style={{ minWidth: '200px' }}
          />

          <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginLeft: '1rem' }}>
            Status:
          </span>
          <HTMLSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { label: 'Active Players', value: 'active' },
              { label: 'Retired Players', value: 'retired' },
              { label: 'All Players', value: 'all' }
            ]}
            style={{ minWidth: '150px' }}
          />
        </div>

        <div style={{ display: 'grid', gap: '1rem' }}>
          {filteredPlayerStats.map((player, index) => (
            <div key={player.displayName} className="player-stats-card" style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr auto',
              gap: '1rem',
              alignItems: 'center',
              padding: '1rem',
              background: index < 3 ? 'linear-gradient(135deg, var(--bg-tertiary), var(--bg-secondary))' : 'var(--bg-secondary)',
              borderRadius: 'var(--radius)',
              border: index === 0 ? '2px solid var(--success)' : '1px solid var(--border)',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onClick={() => handlePlayerClick(player)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow)';
            }}
            >
              {/* Rank */}
              <div className="player-rank" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: getRankColor(index),
                  lineHeight: 1
                }}>
                  {getRankIcon(index)}
                </div>
                <div
                  title={getPlayerEloRankChange30d(player) === null ? undefined : 'ELO rank change (30d)'}
                  style={{
                    marginTop: '0.2rem',
                    minHeight: '0.7rem',
                    fontSize: '0.65rem',
                    lineHeight: 1,
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    opacity: 0.75,
                    color: getPlayerEloRankChange30d(player) > 0
                      ? '#5b7a69'
                      : getPlayerEloRankChange30d(player) < 0
                        ? '#866666'
                        : 'var(--text-muted)'
                  }}
                >
                  {getPlayerEloRankChange30d(player) === null
                    ? ''
                    : getPlayerEloRankChange30d(player) === 0
                      ? '·'
                      : `${getPlayerEloRankChange30d(player) > 0 ? '↑' : '↓'}${Math.abs(getPlayerEloRankChange30d(player))}`}
                </div>
              </div>

              {/* Player Info */}
              <div className="player-info">
                <div className="player-name" style={{
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  marginBottom: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {player.displayName}
                  {playerStatuses[player.name] === 'retired' && (
                    <Tag minimal intent={Intent.WARNING} style={{ fontSize: '0.7rem' }}>Retired</Tag>
                  )}
                </div>
                <div className="player-meta" style={{
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}>
                  <span>{player.tournamentsPlayed} ACTs</span>
                  <span>{player.racesPlayed} races</span>
                  <span>{player.avgPoints} avg pts/ACT</span>
                  <span>{player.winRate}% win rate</span>
                  <span>{player.jerseySwapRate}% Jersey Swap</span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="stats-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: '1rem',
                textAlign: 'center',
                minWidth: 0
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    POINTS
                  </div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--accent-primary)' }}>
                    {player.totalPoints}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    JERSEY SWAPS
                  </div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--accent-secondary)' }}>
                    {player.jerseySwaps}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    WINS
                  </div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--success)' }}>
                    {player.wins}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    ELO
                  </div>
                  <div className="player-elo" style={{
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    color: player.hasEloRating ? player.eloTier.color : '#9E9E9E'
                  }}>
                    {player.hasEloRating ? player.elo : 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', opacity: 0.8 }}>
                    30D Change
                  </div>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    opacity: 0.78,
                    color: getPlayerEloChange30d(player) === null
                      ? 'var(--text-muted)'
                      : getPlayerEloChange30d(player) > 0
                        ? '#7ea28f'
                        : getPlayerEloChange30d(player) < 0
                          ? '#a18787'
                          : 'var(--text-secondary)'
                  }}>
                    {getPlayerEloChange30d(player) === null
                      ? 'N/A'
                      : `${getPlayerEloChange30d(player) > 0 ? '+' : ''}${getPlayerEloChange30d(player)}`}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Player Details Modal */}
      <Dialog
        isOpen={isPlayerModalOpen}
        onClose={closePlayerModal}
        title="Player Statistics"
        style={{ width: '600px' }}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
      >
        <DialogBody>
          {selectedPlayer && (
            <div>
              {/* Player Header */}
              <div style={{
                textAlign: 'center',
                marginBottom: '2rem',
                padding: '1.5rem',
                background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)'
              }}>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--accent-primary)'
                }}>
                  {selectedPlayer.displayName}
                </div>
              </div>

              {/* Core Statistics */}
              <div style={{ marginBottom: '2rem' }}>
                <H3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Icon icon="chart" size={18} />
                  Core Statistics
                </H3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '1.5rem'
                }}>
                  <Card style={{
                    padding: '1.5rem',
                    textAlign: 'center',
                    background: '#fafafa',
                    border: '1px solid var(--accent-primary)'
                  }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--accent-primary)', marginBottom: '0.75rem' }}>
                      {selectedPlayer.totalPoints}
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'black', marginBottom: '0.25rem' }}>
                      Total Points
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>
                      {selectedPlayer.avgPoints} avg per ACT
                    </div>
                  </Card>

                  <Card style={{
                    padding: '1.5rem',
                    textAlign: 'center',
                    background: '#fafafa',
                    border: '1px solid var(--success)'
                  }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--success)', marginBottom: '0.75rem' }}>
                      {selectedPlayer.wins}
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'black', marginBottom: '0.25rem' }}>
                      Tournament Wins
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>
                      {selectedPlayer.winRate}% win rate
                    </div>
                  </Card>

                  <Card style={{
                    padding: '1.5rem',
                    textAlign: 'center',
                    background: '#fafafa',
                    border: '1px solid var(--accent-secondary)'
                  }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--accent-secondary)', marginBottom: '0.75rem' }}>
                      {selectedPlayer.jerseySwaps}
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'black', marginBottom: '0.25rem' }}>
                      Jersey Swaps
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>
                      {selectedPlayer.jerseySwapRate}% top-2 rate
                    </div>
                  </Card>

                  <Card style={{
                    padding: '1.5rem',
                    textAlign: 'center',
                    background: '#fafafa',
                    border: '1px solid var(--warning)'
                  }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--warning)', marginBottom: '0.75rem' }}>
                      {selectedPlayer.racesPlayed}
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'black', marginBottom: '0.25rem' }}>
                      Races Played
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>
                      {selectedPlayer.tournamentsPlayed} ACT{selectedPlayer.tournamentsPlayed !== 1 ? 's' : ''}
                    </div>
                  </Card>

                  <Card style={{
                    padding: '1.5rem',
                    textAlign: 'center',
                    background: '#fafafa',
                    border: '2px solid var(--accent-secondary)'
                  }}>
                    <div style={{
                      fontSize: '1.8rem',
                      fontWeight: 700,
                      color: 'var(--accent-primary)',
                      marginBottom: '0.75rem',
                      textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}>
                      {selectedPlayer.favoriteCharacter}
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'black', marginBottom: '0.25rem' }}>
                      Favorite Character
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>
                      {selectedPlayer.favoriteCharacterCount} time{selectedPlayer.favoriteCharacterCount !== 1 ? 's' : ''} played
                    </div>
                  </Card>

                  <Card style={{
                    padding: '1.5rem',
                    textAlign: 'center',
                    background: '#fafafa',
                    border: '2px solid var(--error)'
                  }}>
                    <div style={{
                      fontSize: '1.8rem',
                      fontWeight: 700,
                      color: 'var(--error)',
                      marginBottom: '0.75rem',
                      textShadow: '0 1px 3px rgba(0,0,0,0.15)'
                    }}>
                      {selectedPlayer.biggestRival}
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'black', marginBottom: '0.25rem' }}>
                      Biggest Rival
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>
                      {selectedPlayer.biggestRival === 'None'
                        ? 'No 60/40 to 40/60 rivalry found'
                        : `${selectedPlayer.biggestRivalRaces} races, ${selectedPlayer.biggestRivalWins}-${selectedPlayer.biggestRivalLosses}`}
                    </div>
                  </Card>
                </div>
              </div>

              {/* Performance Breakdown */}
              <div style={{ marginBottom: '2rem' }}>
                <H3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Icon icon="timeline-bar-chart" size={18} />
                  Performance Breakdown
                </H3>
                <div style={{
                  display: 'grid',
                  gap: '1rem'
                }}>
                  {/* Tournament Performance */}
                  <Card style={{ padding: '1rem' }}>
                    <H4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Icon icon="crown" size={16} />
                      ACT Finishes
                    </H4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', textAlign: 'center' }}>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)', marginBottom: '0.25rem' }}>
                          {selectedPlayer.tournamentFirst}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>1st Place</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {selectedPlayer.tournamentFirstPct}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-secondary)', marginBottom: '0.25rem' }}>
                          {selectedPlayer.tournamentSecond}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>2nd Place</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {selectedPlayer.tournamentSecondPct}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)', marginBottom: '0.25rem' }}>
                          {selectedPlayer.tournamentThird}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>3rd Place</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {selectedPlayer.tournamentThirdPct}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                          {selectedPlayer.tournamentFourth}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>4th Place</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {selectedPlayer.tournamentFourthPct}%
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Race Performance */}
                  <Card style={{ padding: '1rem' }}>
                    <H4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Icon icon="drive-time" size={16} />
                      Individual Race Finishes
                    </H4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', textAlign: 'center' }}>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)', marginBottom: '0.25rem' }}>
                          {selectedPlayer.raceFirst}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>1st Place</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {selectedPlayer.raceFirstPct}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-secondary)', marginBottom: '0.25rem' }}>
                          {selectedPlayer.raceSecond}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>2nd Place</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {selectedPlayer.raceSecondPct}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)', marginBottom: '0.25rem' }}>
                          {selectedPlayer.raceThird}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>3rd Place</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {selectedPlayer.raceThirdPct}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                          {selectedPlayer.raceFourth}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>4th Place</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {selectedPlayer.raceFourthPct}%
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* ELO Rating */}
                  <Card style={{ padding: '1rem' }}>
                    <H4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Icon icon="numerical" size={16} />
                      ELO Rating
                    </H4>
                    <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                      <div style={{
                        fontSize: '3rem',
                        fontWeight: 700,
                        color: selectedPlayer.hasEloRating ? selectedPlayer.eloTier.color : '#9E9E9E',
                        marginBottom: '0.5rem'
                      }}>
                        {selectedPlayer.hasEloRating ? selectedPlayer.elo : 'N/A'}
                      </div>
                      {selectedPlayer.firebaseEloStats && (
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                          ELO Change (30d):{' '}
                          {selectedPlayer.firebaseEloStats.change30d === null ? (
                            <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                          ) : (
                            <span style={{
                              fontWeight: 700,
                              color: selectedPlayer.firebaseEloStats.change30d > 0
                                ? 'var(--success)'
                                : selectedPlayer.firebaseEloStats.change30d < 0
                                  ? '#d14343'
                                  : 'var(--text-secondary)'
                            }}>
                              {selectedPlayer.firebaseEloStats.change30d > 0 ? '+' : ''}
                              {selectedPlayer.firebaseEloStats.change30d}
                            </span>
                          )}
                          {selectedPlayer.firebaseEloStats.change30dPartialWindow && selectedPlayer.firebaseEloStats.change30d !== null && (
                            <span style={{ color: 'var(--text-muted)' }}> (partial window)</span>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedPlayer.hasFirebaseHistory ? (
                      <div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                          ELO History ({selectedPlayer.firebaseEloStats.historyCount} updates)
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}>
                          <span>High: {selectedPlayer.firebaseEloStats.high}</span>
                          <span>Current: {selectedPlayer.firebaseEloStats.current}</span>
                          <span>Low: {selectedPlayer.firebaseEloStats.low}</span>
                        </div>
                      </div>
                    ) : selectedPlayer.firebaseEloStats && selectedPlayer.firebaseEloStats.fromEloNode ? (
                      <div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                          Current ELO Rating
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                          textAlign: 'center'
                        }}>
                          No match history available yet
                        </div>
                      </div>
                    ) : selectedPlayer.eloHistory && selectedPlayer.eloHistory.length > 1 ? (
                      <div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                          ELO Range ({selectedPlayer.eloHistory.length - 1} tournaments)
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}>
                          <span>High: {Math.max(...selectedPlayer.eloHistory.map(h => h.elo))}</span>
                          <span>Current: {selectedPlayer.elo}</span>
                          <span>Low: {Math.min(...selectedPlayer.eloHistory.map(h => h.elo))}</span>
                        </div>
                      </div>
                    ) : null}
                  </Card>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ textAlign: 'center' }}>
                <Button
                  intent={Intent.PRIMARY}
                  onClick={closePlayerModal}
                  large
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogBody>
      </Dialog>

      {/* Admin Password Dialog */}
      <Dialog
        isOpen={isAdminModalOpen}
        onClose={closeAdminModal}
        title="Admin Access Required"
        icon="lock"
        style={{ width: '400px' }}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
      >
        <DialogBody>
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              Enter the admin password to access the ELO management tools:
            </p>
            <InputGroup
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Enter admin password..."
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAdminPasswordSubmit();
                }
              }}
              autoFocus
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={closeAdminModal}>
            Cancel
          </Button>
          <Button
            intent={Intent.PRIMARY}
            onClick={handleAdminPasswordSubmit}
            disabled={!adminPassword.trim()}
          >
            Access Admin Panel
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default Analytics;
